import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import {
  Home, Users, Settings, LogOut, Menu, X, Wrench, Package,
  Briefcase, BookOpen, GraduationCap, Shield, ChevronDown,
  ChevronRight, FileText, ClipboardList, BarChart2,
  Calendar, Clock, Receipt, Factory, Hammer, Download,
  UserCircle, AlertCircle
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

type MenuItem = {
  label: string;
  icon: any;
  path?: string;
  permKey?: string;
  children?: { label: string; path: string; icon: any; permKey?: string }[];
  adminOnly?: boolean;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isAdmin, hasPermission, user } = useAuth();

  const getMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [
      { icon: Home, label: 'Mission Control', path: '/' },
    ];

    // Work Side
    if (isAdmin || hasPermission('can_view_sales')) {
      items.push({
        icon: Briefcase, label: 'Project Sales',
        children: [
          { label: 'Pipeline', path: '/worklog/sales', icon: BarChart2 },
        ]
      });
    }

    if (isAdmin || hasPermission('can_view_spares') || hasPermission('can_view_inventory')) {
      const partsChildren: any[] = [];
      if (isAdmin || hasPermission('can_view_spares')) partsChildren.push({ label: 'Parts Sales', path: '/worklog/parts', icon: ClipboardList });
      if (isAdmin || hasPermission('can_view_inventory')) partsChildren.push({ label: 'Inventory', path: '/inventory', icon: Package });
      items.push({ icon: Wrench, label: 'Parts & Inventory', children: partsChildren });
    }

    if (isAdmin || hasPermission('can_view_production')) {
      items.push({
        icon: Factory, label: 'Production',
        children: [
          { label: 'Production Board', path: '/production', icon: Hammer },
          { label: 'Gantt Chart', path: '/production/gantt', icon: BarChart2 },
          ...(isAdmin ? [] : [{ label: 'My Jobs', path: '/my-jobs', icon: ClipboardList }]),
        ]
      });
    }

    // Staff Side — always visible to everyone
    const staffChildren: any[] = [
      { label: 'My Dashboard', path: '/staff-hub', icon: UserCircle },
      { label: 'Holidays', path: '/holidays', icon: Calendar },
      { label: 'Timesheets', path: '/timesheets', icon: Clock },
      { label: 'Payslips', path: '/payslips', icon: Receipt },
      { label: 'My Profile', path: '/profile', icon: Users },
    ];
    items.push({ icon: Users, label: 'Staff Hub', children: staffChildren });

    // Knowledge Base
    items.push({
      icon: BookOpen, label: 'Knowledge Base',
      children: [
        { label: 'SOPs & Policies', path: '/staff-hub/sops', icon: FileText },
        { label: 'Training Matrix', path: '/staff-hub/training', icon: GraduationCap },
      ]
    });

    // Admin Only
    if (isAdmin) {
      items.push({ icon: Shield, label: 'Admin Portal', path: '/admin', adminOnly: true });
      items.push({ icon: Settings, label: 'Settings', path: '/settings', adminOnly: true });
      items.push({ icon: Download, label: 'Data & Sync', path: '/sync', adminOnly: true });
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Auto-open folder containing active route
  useEffect(() => {
    menuItems.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child =>
          location.pathname === child.path?.split('?')[0]
        );
        if (isChildActive) {
          setOpenFolders(prev => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
    window.location.reload();
  };

  const toggleFolder = (label: string) => {
    setOpenFolders(prev => ({ ...prev, [label]: !prev[label] }));
  };

  if (location.pathname === '/login' || location.pathname === '/update-password') {
    return <main className="w-full">{children}</main>;
  }

  if (!user) {
    return <main className="w-full">{children}</main>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-20 md:hidden transition-opacity ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`fixed md:sticky top-0 h-screen w-64 bg-slate-900 text-white transition-transform duration-300 z-30 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        {/* Brand */}
        <div className="p-5 border-b border-slate-800 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold tracking-wider bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">VCSP</h1>
            <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] mt-0.5">
              {isAdmin ? '⚡ Admin Mode' : 'Staff Portal'}
            </p>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-slate-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* User Card */}
        <div className="px-4 py-3 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
              {profile?.first_name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {profile?.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : profile?.email?.split('@')[0]}
              </p>
              <p className="text-[10px] text-slate-500 capitalize">{profile?.role || 'Staff'}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {menuItems.map((item) => {
            const isOpen = openFolders[item.label] || false;

            if (item.children) {
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    onClick={() => toggleFolder(item.label)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-slate-800 text-slate-400 hover:text-white"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={18} />
                      <span>{item.label}</span>
                    </div>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>

                  {isOpen && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l border-slate-700/50 pl-3 animate-fade-in">
                      {item.children.map(child => {
                        const isActive = location.pathname === child.path?.split('?')[0];
                        return (
                          <Link
                            key={child.label}
                            to={child.path}
                            onClick={() => setIsSidebarOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all
                            ${isActive
                                ? 'text-blue-400 bg-blue-500/10 font-semibold'
                                : 'text-slate-500 hover:text-white hover:bg-slate-800'
                              }`}
                          >
                            <child.icon size={15} />
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path || '/'}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                ${isActive
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/20'
                    : item.adminOnly
                      ? 'text-amber-400/70 hover:bg-slate-800 hover:text-amber-300'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-red-400/70 hover:bg-slate-800 hover:text-red-400 transition-colors"
          >
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="md:hidden bg-white border-b border-gray-200 p-4 flex items-center gap-4 shadow-sm">
          <button onClick={() => setIsSidebarOpen(true)} className="text-gray-600 hover:text-gray-900">
            <Menu size={24} />
          </button>
          <span className="font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">VCSP System</span>
        </header>
        <main className="flex-1 overflow-auto p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;